/**
 * Sell listing create workflow — single owner for seller create orchestration.
 *
 * Route shells (`/sell`) own UI state and presentation; submit goes through:
 * - {@link createSellListing} — POST /api/sell/listings then images
 * - {@link submitBulkSellListings} — bulk loop bound to the same create path
 * - payload builders in `sellListingFormPayload.ts`
 *
 * Review / resubmit / price decision stay in `lib/listing-review-rounds`.
 */

import { createSellListing } from "@/lib/listing/createSellListing";
import {
  runBulkSellListingSubmit,
  type RunBulkSellListingSubmitArgs,
  type RunBulkSellListingSubmitResult,
} from "@/lib/listing/sellListingBulkSubmit";

export { createSellListing } from "@/lib/listing/createSellListing";
export type {
  CreateSellListingParams,
  CreateSellListingFormPayload,
} from "@/lib/listing/createSellListing";
export {
  formPayloadFromSellForm,
  formPayloadFromBulkDraft,
} from "@/lib/listing/sellListingFormPayload";
export {
  runBulkSellListingSubmit,
  type RunBulkSellListingSubmitArgs,
  type RunBulkSellListingSubmitResult,
  type BulkSubmitResultPatch,
} from "@/lib/listing/sellListingBulkSubmit";
export {
  assertSellListingAbsolutePrice,
  assertSellListingFreeText,
  SELL_LISTING_CREATE_EDIT_DIFFERENCES,
} from "@/lib/listing/sellListingContract";
export {
  resolveListingSpecPluginKind,
  resolveListingSpecStepTwoUi,
  getListingSpecFormValidationMessage,
  buildListingAttributesForSubmit,
} from "@/lib/listing/listingSpecPlugin";

/**
 * Bulk create entry for `/sell` — always uses {@link createSellListing}
 * (API insert + policy). Tests may call `runBulkSellListingSubmit` with a mock.
 */
export function submitBulkSellListings(
  args: Omit<RunBulkSellListingSubmitArgs, "createListing">,
): Promise<RunBulkSellListingSubmitResult> {
  return runBulkSellListingSubmit({
    ...args,
    createListing: createSellListing,
  });
}
